<template>
    <div
        class="w-full min-h-32 p-5 rounded-md border border-gray-300 bg-white absolute top-12 left-0 shadow-xl"
    >
        <activix-pills
            class="text-sm mb-4"
            :pills="searchTypes"
            :model-value="value"
            @update:model-value="updateSelectedSearchType"
        />
        <section class="max-h-156 overflow-y-scroll">
            <search-bar-item
                :search-item="searchObject"
                @click-item="onClickItem"
                v-if="validSearchValue"
            />
            <div class="space-y-2.5" v-if="showRecentHistory">
                <div v-if="recentsSearch.length">
                    <div class="text-sm text-gray-600 mb-2">
                        {{ $t('search.recentSearch') }}
                    </div>
                    <search-bar-item
                        :clearable="true"
                        :search-item="item"
                        :key="item.id"
                        @clear="clearRecent"
                        @click-item="onClickItem"
                        v-for="item in recentsSearch"
                    />
                </div>
                <div v-if="recentsViewed.length">
                    <div class="text-sm text-gray-600 mb-2">
                        {{ $t('search.recentViewed') }}
                    </div>
                    <search-bar-item
                        :clearable="true"
                        :search-item="item"
                        :key="item.id"
                        @clear="clearRecent"
                        @click-item="onClickItem"
                        v-for="item in recentsViewed"
                    />
                </div>
            </div>
            <search-bar-item
                :search-item="item"
                :key="item.id"
                @click-item="onClickItem"
                v-for="item in resultsMapped"
            />
            <template v-if="$wait.is('fetching.search-bar')">
                <search-bar-item-skeleton :key="index" v-for="index in 3" />
            </template>
            <div
                class="text-center text-gray-500 my-2"
                v-else-if="!results.length && !validSearchValue && !recents.length"
            >
                {{ $t('search.startTyping') }}
            </div>
            <div
                class="text-center my-2"
                v-if="results.length && value === 'recent' && !validSearchValue"
            >
                <activix-button type="white" @click="$emit('search', false)">
                    {{ $t('search.viewAll') }}
                </activix-button>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
    import { ActivixPills } from '@/components/activix-ui/index.ts';
    import SearchBarItem from '@/components/search/bar/SearchBarItem.vue';
    import SearchBarItemSkeleton from '@/components/search/bar/SearchBarItemSkeleton.vue';
    import LeadType from '@/entities/LeadType.js';
    import { SearchBar, SearchBarItemType, SearchBarModelType } from '@/entities/SearchBar.ts';
    import type { SearchBarItemObj } from '@/entities/SearchBar.ts';
    import Division from '@/entities/Division.js';
    import { defineComponent } from 'vue';

    interface State {
        searchHistory: SearchBarItemObj[];
    }

    export default defineComponent({
        components: {
            SearchBarItemSkeleton,
            SearchBarItem,
            ActivixPills,
        },

        emits: ['input', 'on-click-result', 'search'],

        props: {
            accountId: {
                type: Number,
                default: undefined,
            },
            value: {
                type: String as () => SearchBarModelType,
                default: '',
            },
            results: {
                type: Array as () => SearchBarItemObj[],
                required: true,
                default: () => [],
            },
            search: {
                type: String as () => string | null,
                default: '',
            },
            searchTypes: {
                type: Array as () => Array<{ label: string; name: string; value: string }>,
                default: () => [],
            },
            userId: {
                type: Number,
                default: undefined,
            },
        },

        data(): State {
            return {
                searchHistory: [],
            };
        },

        computed: {
            showRecentHistory(): boolean {
                return (
                    !this.validSearchValue &&
                    !this.resultsMapped.length &&
                    !this.$wait.is('fetching.search-bar')
                );
            },

            validSearchValue(): boolean {
                return !!(this.search && this.search.trim());
            },

            searchObject(): SearchBarItemObj {
                if (this.value === SearchBarModelType.CUSTOMER) {
                    return {
                        accountId: this.accountId,
                        id: this.search,
                        type: SearchBarModelType.CUSTOMER,
                        title: this.search,
                        itemType: SearchBarItemType.SEARCH,
                        userId: this.userId,
                    } as SearchBarItemObj;
                }

                return {
                    id: this.search,
                    type: this.value as Exclude<SearchBarModelType, SearchBarModelType.CUSTOMER>,
                    title: this.search,
                    itemType: SearchBarItemType.SEARCH,
                };
            },

            resultsMapped(): SearchBarItemObj[] {
                return this.results.map((data: any) => {
                    if (this.value === SearchBarModelType.CUSTOMER) {
                        return {
                            accountId: this.accountId,
                            createdAt: data.created_at,
                            description: data.email ?? undefined,
                            id: data.id,
                            itemType: SearchBarItemType.RESULT,
                            leadId: data.lead_id,
                            other: data.phone ?? undefined,
                            title: data.full_name,
                            type: SearchBarModelType.CUSTOMER,
                            userId: this.userId,
                        } as SearchBarItemObj;
                    }

                    const mappedResult: SearchBarItemObj = {
                        id: data.id,
                        leadId: data.lead_id,
                        createdAt: data.created_at,
                        type: this.value as Exclude<
                            SearchBarModelType,
                            SearchBarModelType.CUSTOMER
                        >,
                        itemType: SearchBarItemType.RESULT,
                    };

                    if (
                        [SearchBarModelType.LEAD, SearchBarModelType.RECENT].includes(
                            this.value as SearchBarModelType
                        )
                    ) {
                        const vehicleStr = this.joinString(
                            data.first_wanted_vehicle?.make,
                            data.first_wanted_vehicle?.model,
                            data.first_wanted_vehicle?.year
                        );
                        mappedResult.leadId = data.id;
                        mappedResult.title = `${data.first_name} ${data.last_name}`.trim();
                        mappedResult.icon = LeadType.getIcon(data.lead_type_id);
                        mappedResult.subTitle = Division.getTranslation(data.division_id) as string;
                        mappedResult.description = vehicleStr;
                        mappedResult.status = data.status;
                    } else if (this.value === SearchBarModelType.VEHICLE) {
                        mappedResult.createdAt = data.lead.created_at;
                        mappedResult.leadId = data.lead.id;
                        mappedResult.title = this.joinString(
                            data.make,
                            data.model,
                            data.year,
                            data.stock ? `#${data.stock}` : ''
                        );
                        mappedResult.subTitle = data.vin;
                        mappedResult.description = this.joinString(
                            data.order_number ? `Order: ${data.order_number}` : '',
                            data.offer_number ? `Offer: ${data.offer_number}` : ''
                        );
                        mappedResult.other = this.joinString(
                            data.lead?.first_name,
                            data.lead?.last_name
                        );
                    } else if (this.value === SearchBarModelType.USER) {
                        mappedResult.title = `${data.first_name} ${data.last_name}`;
                    } else if (this.value === SearchBarModelType.NOTE) {
                        const content = data.content.length ? data.content.join(' | ') : '';

                        mappedResult.description =
                            content.length > 100 ? `${content.substring(0, 100)}...` : content;

                        mappedResult.title = this.joinString(
                            data.lead.first_name,
                            data.lead.last_name
                        );
                    }

                    return mappedResult;
                });
            },

            recents(): SearchBarItemObj[] {
                return this.searchHistory.filter((item: SearchBarItemObj) => {
                    if (item.type !== this.value) {
                        return false;
                    }

                    if (item.type !== SearchBarModelType.CUSTOMER) {
                        return true;
                    }

                    return (
                        Number(item.accountId) === Number(this.accountId) &&
                        Number(item.userId) === Number(this.userId)
                    );
                });
            },

            recentsSearch(): SearchBarItemObj[] {
                return this.recents.filter(
                    (item: SearchBarItemObj) => item.itemType === SearchBarItemType.SEARCH
                );
            },

            recentsViewed(): SearchBarItemObj[] {
                return this.recents.filter(
                    (item: SearchBarItemObj) => item.itemType === SearchBarItemType.RESULT
                );
            },
        },

        methods: {
            clearRecent(item: SearchBarItemObj): void {
                SearchBar.delete(
                    item.id,
                    item.type,
                    item.itemType,
                    item.type === SearchBarModelType.CUSTOMER ? item.userId : undefined,
                    item.type === SearchBarModelType.CUSTOMER ? item.accountId : undefined
                );
                this.searchHistory = SearchBar.all();
            },

            onClickItem(searchItem: SearchBarItemObj, openExternal: boolean = false): void {
                if (searchItem.itemType === SearchBarItemType.SEARCH) {
                    this.$emit('search', searchItem, openExternal);
                    this.searchHistory = SearchBar.all();

                    return;
                }

                let route = {};
                const leadRoutes = [
                    SearchBarModelType.LEAD,
                    SearchBarModelType.RECENT,
                    SearchBarModelType.NOTE,
                    SearchBarModelType.VEHICLE,
                ];

                if (leadRoutes.includes(searchItem.type)) {
                    route = { name: 'leads.update', params: { id: searchItem.leadId } };
                } else if (searchItem.type === SearchBarModelType.USER) {
                    route = { name: 'users.update', params: { id: searchItem.id } };
                } else if (searchItem.type === SearchBarModelType.CUSTOMER) {
                    route = { name: 'customers.profile', params: { id: searchItem.id } };
                }

                this.$emit('on-click-result', route, openExternal);
                const searchBarItem: SearchBarItemObj = { ...searchItem };

                SearchBar.set(searchBarItem);
                this.searchHistory = SearchBar.all();
            },

            updateSelectedSearchType(event: any): void {
                this.$emit('input', event);
            },

            focusFirstResult(): boolean {
                const firstResult = (this.$el as HTMLElement).querySelector<HTMLElement>(
                    '[data-testid="search-bar-item"]'
                );

                if (!firstResult) {
                    return false;
                }

                firstResult.focus();

                return true;
            },

            joinString(...args: string[] | null[]): string {
                return [...args].filter(Boolean).join(' ');
            },
        },

        mounted(): void {
            this.searchHistory = SearchBar.all();
        },
    });
</script>
